import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from "recharts";
import { format } from "date-fns";

interface LabResult {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string;
  source_document_id: string;
  subcategory_id: string;
}

interface SubcategoryData {
  id: string;
  name: string;
  resultsByMonth: {
    [monthYear: string]: any[];
  };
  allResults: LabResult[];
}

type ReportPeriod = "current_month" | "current_year" | "last_year" | "last_3_years" | "all";

interface SubcategoryChartModalProps {
  subcategory: SubcategoryData;
  period: ReportPeriod;
  onClose: () => void;
}

// Função auxiliar para extrair o nome base e o lado (Direita/Esquerda)
const extractTestNameInfo = (testName: string): { baseName: string; side: 'direita' | 'esquerda' | null } => {
  // Primeiro, tentar formato com hífen: "Nome - Direita" ou "Nome - Esquerda"
  const direitaMatch = testName.match(/^(.+?)\s*-\s*Direita$/i);
  if (direitaMatch) {
    return { baseName: direitaMatch[1].trim(), side: 'direita' };
  }
  const esquerdaMatch = testName.match(/^(.+?)\s*-\s*Esquerda$/i);
  if (esquerdaMatch) {
    return { baseName: esquerdaMatch[1].trim(), side: 'esquerda' };
  }
  
  // Depois, tentar formato com parênteses: "Nome (direita)" ou "Nome (esquerda)"
  const direitaParenMatch = testName.match(/^(.+?)\s*\(direita\)$/i);
  if (direitaParenMatch) {
    return { baseName: direitaParenMatch[1].trim(), side: 'direita' };
  }
  const esquerdaParenMatch = testName.match(/^(.+?)\s*\(esquerda\)$/i);
  if (esquerdaParenMatch) {
    return { baseName: esquerdaParenMatch[1].trim(), side: 'esquerda' };
  }
  
  return { baseName: testName, side: null };
};

// Detectar se é um registro de pressão arterial (Sistólica/Diastólica)
const extractBloodPressureInfo = (testName: string): { isBloodPressure: boolean; bpSide: 'sistolica' | 'diastolica' | null } => {
  const lower = testName.toLowerCase();
  if (lower.includes('sistólica') || lower.includes('sistolica')) {
    return { isBloodPressure: true, bpSide: 'sistolica' };
  }
  if (lower.includes('diastólica') || lower.includes('diastolica')) {
    return { isBloodPressure: true, bpSide: 'diastolica' };
  }
  return { isBloodPressure: false, bpSide: null };
};

export const SubcategoryChartModal = ({ subcategory, period, onClose }: SubcategoryChartModalProps) => {
  // Processar resultados para detectar pares e pressão arterial
  const results = subcategory.allResults.filter((r) => r.value !== null);
  
  // Verificar se há pares (Direita/Esquerda)
  const hasPair = results.some(r => {
    const { side } = extractTestNameInfo(r.test_name);
    return side !== null;
  });
  
  // Verificar se é pressão arterial
  const hasBloodPressure = results.some(r => {
    const { isBloodPressure } = extractBloodPressureInfo(r.test_name);
    return isBloodPressure;
  });

  // Criar dados do gráfico agrupados por data
  const createChartData = () => {
    const dataMap = new Map<string, {
      date: string;
      fullDate: string;
      timestamp: number;
      direita?: number | null;
      esquerda?: number | null;
      sistolica?: number | null;
      diastolica?: number | null;
      value?: number | null;
      testName: string;
      unit: string | null;
      refLow: number | null;
      refHigh: number | null;
    }>();

    results.forEach(r => {
      const dateObj = new Date(r.collected_at);
      const dateKey = format(dateObj, "MM/yyyy");
      const fullDateKey = format(dateObj, "dd/MM/yyyy");
      
      if (!dataMap.has(dateKey)) {
        dataMap.set(dateKey, {
          date: dateKey,
          fullDate: fullDateKey,
          timestamp: dateObj.getTime(),
          testName: r.test_name,
          unit: r.unit,
          refLow: r.ref_low,
          refHigh: r.ref_high,
        });
      }
      
      const entry = dataMap.get(dateKey)!;
      
      if (hasBloodPressure) {
        const { bpSide } = extractBloodPressureInfo(r.test_name);
        if (bpSide === 'sistolica') {
          entry.sistolica = r.value;
        } else if (bpSide === 'diastolica') {
          entry.diastolica = r.value;
        }
      } else if (hasPair) {
        const { side } = extractTestNameInfo(r.test_name);
        if (side === 'direita') {
          entry.direita = r.value;
        } else if (side === 'esquerda') {
          entry.esquerda = r.value;
        }
      } else {
        entry.value = r.value;
      }
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const chartData = createChartData();
  const latest = chartData[chartData.length - 1];

  // Calcular estatísticas
  const calculateStats = () => {
    if (hasBloodPressure) {
      const sistolicaValues = chartData.map(d => d.sistolica).filter((v): v is number => v !== null && v !== undefined);
      const diastolicaValues = chartData.map(d => d.diastolica).filter((v): v is number => v !== null && v !== undefined);
      const latestSistolica = sistolicaValues[sistolicaValues.length - 1];
      const latestDiastolica = diastolicaValues[diastolicaValues.length - 1];
      const avgSistolica = sistolicaValues.length > 0 ? sistolicaValues.reduce((sum, v) => sum + v, 0) / sistolicaValues.length : 0;
      const avgDiastolica = diastolicaValues.length > 0 ? diastolicaValues.reduce((sum, v) => sum + v, 0) / diastolicaValues.length : 0;
      
      return {
        latest: latestSistolica && latestDiastolica ? `${latestSistolica.toFixed(0)}/${latestDiastolica.toFixed(0)}` : 'N/A',
        average: `${avgSistolica.toFixed(0)}/${avgDiastolica.toFixed(0)}`,
        unit: 'mmHg',
        count: Math.max(sistolicaValues.length, diastolicaValues.length)
      };
    } else if (hasPair) {
      const direitaValues = chartData.map(d => d.direita).filter((v): v is number => v !== null && v !== undefined);
      const esquerdaValues = chartData.map(d => d.esquerda).filter((v): v is number => v !== null && v !== undefined);
      const latestDireita = direitaValues[direitaValues.length - 1];
      const latestEsquerda = esquerdaValues[esquerdaValues.length - 1];
      const avgDireita = direitaValues.length > 0 ? direitaValues.reduce((sum, v) => sum + v, 0) / direitaValues.length : 0;
      const avgEsquerda = esquerdaValues.length > 0 ? esquerdaValues.reduce((sum, v) => sum + v, 0) / esquerdaValues.length : 0;
      
      return {
        latest: latestDireita && latestEsquerda ? `D: ${latestDireita.toFixed(2)} | E: ${latestEsquerda.toFixed(2)}` : 'N/A',
        average: `D: ${avgDireita.toFixed(2)} | E: ${avgEsquerda.toFixed(2)}`,
        unit: latest?.unit || '',
        count: Math.max(direitaValues.length, esquerdaValues.length)
      };
    } else {
      const values = chartData.map(d => d.value).filter((v): v is number => v !== null && v !== undefined);
      const latestValue = values[values.length - 1];
      const average = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
      
      return {
        latest: latestValue !== undefined ? latestValue.toFixed(2) : 'N/A',
        average: average.toFixed(2),
        unit: latest?.unit || '',
        count: values.length
      };
    }
  };

  const stats = calculateStats();

  // Calcular domínio do eixo Y
  const getAllValues = () => {
    if (hasBloodPressure) {
      const sistolicaValues = chartData.map(d => d.sistolica).filter((v): v is number => v !== null && v !== undefined);
      const diastolicaValues = chartData.map(d => d.diastolica).filter((v): v is number => v !== null && v !== undefined);
      return [...sistolicaValues, ...diastolicaValues];
    } else if (hasPair) {
      const direitaValues = chartData.map(d => d.direita).filter((v): v is number => v !== null && v !== undefined);
      const esquerdaValues = chartData.map(d => d.esquerda).filter((v): v is number => v !== null && v !== undefined);
      return [...direitaValues, ...esquerdaValues];
    }
    return chartData.map(d => d.value).filter((v): v is number => v !== null && v !== undefined);
  };

  const allValues = getAllValues();
  const refLow = latest?.refLow;
  const refHigh = latest?.refHigh;
  
  const yAxisDomain = (() => {
    const values = [...allValues];
    if (refLow !== null) values.push(refLow);
    if (refHigh !== null) values.push(refHigh);
    
    if (values.length === 0) return [0, 100];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || 1;
    
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  })();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Gráfico de Evolução: {subcategory.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Estatísticas */}
          <div className="grid grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Último Valor</p>
              <p className="text-2xl font-bold">
                {stats.latest} {stats.unit && stats.unit !== 'mmHg' ? stats.unit : ''}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{latest?.fullDate}</p>
            </div>
            <div className="border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Média</p>
              <p className="text-2xl font-bold">
                {stats.average} {stats.unit && stats.unit !== 'mmHg' ? stats.unit : ''}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{stats.count} medições</p>
            </div>
            <div className="border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Valores de Referência</p>
              <p className="text-lg font-medium">
                {refLow ?? "—"} - {refHigh ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{latest?.unit}</p>
            </div>
          </div>

          {/* Gráfico */}
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 11 }} domain={yAxisDomain} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value: any, name: string) => {
                  if (value === null || value === undefined) return [null, name];
                  const unit = latest?.unit || "";
                  const label = name === "direita" ? "Direita" : name === "esquerda" ? "Esquerda" 
                    : name === "sistolica" ? "Sistólica" : name === "diastolica" ? "Diastólica" : "Valor";
                  return [`${Number(value).toFixed(2)} ${unit}`, label];
                }}
                labelFormatter={(label) => {
                  const point = chartData.find((d) => d.date === label);
                  return point ? `Data: ${point.fullDate}` : label;
                }}
              />

              {/* Linhas de referência */}
              {refLow !== null && (
                <ReferenceLine
                  y={refLow}
                  stroke="hsl(var(--destructive))"
                  strokeDasharray="3 3"
                  opacity={0.6}
                  label={{ value: "Mín", position: "insideBottomLeft", fontSize: 11 }}
                />
              )}
              {refHigh !== null && (
                <ReferenceLine
                  y={refHigh}
                  stroke="hsl(var(--destructive))"
                  strokeDasharray="3 3"
                  opacity={0.6}
                  label={{ value: "Máx", position: "insideTopLeft", fontSize: 11 }}
                />
              )}

              {/* Renderizar linhas baseado no tipo */}
              {hasBloodPressure ? (
                <>
                  <Line 
                    type="monotone" 
                    dataKey="sistolica" 
                    stroke="#ef4444" 
                    strokeWidth={3} 
                    dot={{ r: 5, fill: "#ef4444" }} 
                    activeDot={{ r: 7 }} 
                    name="Sistólica"
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="diastolica" 
                    stroke="#3b82f6" 
                    strokeWidth={3} 
                    dot={{ r: 5, fill: "#3b82f6" }} 
                    activeDot={{ r: 7 }} 
                    name="Diastólica"
                    connectNulls
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="line" />
                </>
              ) : hasPair ? (
                <>
                  <Line 
                    type="monotone" 
                    dataKey="direita" 
                    stroke="#3b82f6" 
                    strokeWidth={3} 
                    dot={{ r: 5, fill: "#3b82f6" }} 
                    activeDot={{ r: 7 }} 
                    name="Direita"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="esquerda" 
                    stroke="#ef4444" 
                    strokeWidth={3} 
                    dot={{ r: 5, fill: "#ef4444" }} 
                    activeDot={{ r: 7 }} 
                    name="Esquerda"
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="line" />
                </>
              ) : (
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="hsl(var(--primary))"
                  strokeWidth={3}
                  dot={{ r: 5, fill: "hsl(var(--primary))" }}
                  activeDot={{ r: 7 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};
